import React, { useEffect, useState } from "react";
import CompletedLessonsTable from "../components/common/TrainingProgressTable";
import Card from "../components/common/card";
import Button from "../components/common/button";
import Dropdown from "../components/common/dropdown";
import Badge from "../components/common/badge";
import ProgressBar from "../components/common/progressBar";
import CircularProgress from "../components/common/CircularProgress";
import { Clock, Flame, UtensilsCrossed, Wine } from "lucide-react";
import { getUserProgress } from "../services/progress";
import { RestaurantsService } from "../services/Restaurants";
import authService from "../services/authService";

const TrainingProgressPage = () => {
  const [loading, setLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState("");
  const user = authService.getCurrentUser();
  const isEmployee = user?.role === "employee";

  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        const data = await RestaurantsService.getAllRestaurants("name");
        const restaurantList = data.restaurants || data || [];
        setRestaurants(restaurantList);
        if (restaurantList.length > 0) {
          setSelectedRestaurant(restaurantList[0].uuid || restaurantList[0].id);
        }
      } catch (err) {
        console.error("Error fetching restaurants:", err);
      }
    };
    fetchRestaurants();
  }, []);

  useEffect(() => {
    if (selectedRestaurant || !isEmployee) {
      const fetchProgress = async () => {
        try {
          setLoading(true);
          const data = await getUserProgress(isEmployee ? selectedRestaurant : undefined);
          setProgressData(data);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
          setIsInitialLoading(false);
        }
      };

      fetchProgress();
    } else {
      // If we are an employee but have no selected restaurant (no restaurants assigned)
      // we must still stop the initial loading state.
      setIsInitialLoading(false);
    }
  }, [selectedRestaurant, isEmployee]);

  if (isInitialLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF7ED]">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-200 border-t-red-500"></div>
          <p className="text-sm font-bold text-gray-500 animate-pulse">Loading training data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-red-500">Error: {error}</div>;
  }

  if (!progressData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center bg-[#FAF7ED] rounded-2xl border-2 border-dashed border-gray-200">
        <div className="bg-white p-6 rounded-full shadow-sm mb-4">
          <UtensilsCrossed size={40} className="text-gray-300" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">No Restaurants Assigned</h2>
        <p className="text-gray-500 max-w-md">
          You haven't been assigned to any restaurants yet. Please contact your manager or administrator to get started with your training.
        </p>
      </div>
    );
  }

  const metrics = {
    overallProgress: progressData.overAllProgress,
    lessonsCompleted: progressData.completedLessons,
    totalLessons: progressData.allLessons,
    averageScore: progressData.averageScore,
    averageAttempts: progressData.averageAttempts,
  };

  const allLessons = progressData.allLessonsObject || [];

  // Group lessons by unit (all / by category)
  const lessonsByUnitArray = Object.values(
    allLessons.reduce((acc, lesson) => {
      if (!acc[lesson.unit]) {
        acc[lesson.unit] = [];
      }
      acc[lesson.unit].push(lesson);
      return acc;
    }, {})
  );

  const foodLessons = allLessons.filter((lesson) => lesson.category === "food");
  const wineLessons = allLessons.filter((lesson) => lesson.category === "wine");

  const foodLessonsByUnit = Object.values(
    foodLessons.reduce((acc, lesson) => {
      if (!acc[lesson.unit]) acc[lesson.unit] = [];
      acc[lesson.unit].push(lesson);
      return acc;
    }, {})
  );

  const wineLessonsByUnit = Object.values(
    wineLessons.reduce((acc, lesson) => {
      if (!acc[lesson.unit]) acc[lesson.unit] = [];
      acc[lesson.unit].push(lesson);
      return acc;
    }, {})
  );

  const foodCompletedLessons = progressData.food?.foodCompletedLessonsObjects || [];
  const wineCompletedLessons = progressData.wine?.wineCompletedLessonsObjects || [];

  const foodCompletedUUIDs = foodCompletedLessons.map((l) => l.uuid);
  const wineCompletedUUIDs = wineCompletedLessons.map((l) => l.uuid);

  function isUnitCompleted(unitLessons, completedLessonUUIDs) {
    return unitLessons.every((lesson) => completedLessonUUIDs.includes(lesson.uuid));
  }

  const completedFoodUnits = foodLessonsByUnit.filter((unitLessons) =>
    isUnitCompleted(unitLessons, foodCompletedUUIDs)
  ).length;

  const completedWineUnits = wineLessonsByUnit.filter((unitLessons) =>
    isUnitCompleted(unitLessons, wineCompletedUUIDs)
  ).length;

  const foodCount = progressData.food?.foodCompletedLessons || 0;
  const wineCount = progressData.wine?.wineCompletedLessons || 0;
  const totalCompleted = foodCount + wineCount;

  const foodPercentage = totalCompleted ? Math.round((foodCount / totalCompleted) * 100) : 0;
  const winePercentage = totalCompleted ? Math.round((wineCount / totalCompleted) * 100) : 0;

  const baseArc = 251.2;
  const foodArc = totalCompleted ? Math.round((foodCount / totalCompleted) * baseArc) : Math.round(baseArc / 2);
  const wineArc = Math.round(baseArc - foodArc);

  const progressSummary = {
    food: {
      lessonsByUnitArray: foodLessonsByUnit,
      chaptersCompleted: progressData.food?.foodCompletedLessons || 0,
      totalChapters: foodLessons.length,
      averageScore: progressData.food?.foodAverageScoreInPercentage || 0,
      averageAttempts: progressData.food?.foodAverageAttempts || 0,
      unitsCompleted: completedFoodUnits,
      totalUnits: foodLessonsByUnit.length,
      nextChapter: progressData.food?.foodDueLessons?.[0]?.chapter_name || "No upcoming chapters",
    },
    wine: {
      chaptersCompleted: progressData.wine?.wineCompletedLessons || 0,
      totalChapters: wineLessons.length,
      averageScore: progressData.wine?.wineAverageScoreInPercentage || 0,
      averageAttempts: progressData.wine?.wineAverageAttempts || 0,
      unitsCompleted: completedWineUnits,
      totalUnits: wineLessonsByUnit.length,
      nextChapter: progressData.wine?.wineDueLessons?.[0]?.chapter_name || "No upcoming chapters",
    },
  };

  const completedLessons = progressData?.completedLessonsByUser?.map(
    (lesson) => {
      const userProgress = lesson.progress && !Array.isArray(lesson.progress)
        ? lesson.progress
        : Array.isArray(lesson.progress)
          ? lesson.progress[0]
          : null;

      return {
        title: `${lesson.category.charAt(0).toUpperCase() + lesson.category.slice(1)} Unit ${lesson.unit}, Chapter ${lesson.chapter}: ${lesson.chapter_name}`,
        unit: lesson.unit,
        completionDate: userProgress?.completedAt ? new Date(userProgress.completedAt).toLocaleDateString() : "-",
        completedAt: userProgress?.completedAt,
        score: userProgress?.score !== undefined ? `${userProgress.score}%` : "-",
        attempts: Array.isArray(userProgress?.attempts) ? userProgress.attempts.length : (userProgress?.attempts ? userProgress.attempts.length : 0),
      };
    }) || [];

  // Calculate days remaining for next due lesson
  const getDaysRemaining = (dueDate) => {
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = due - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const nextDueLesson = progressData.dueLessons?.[0];
  const daysRemaining = nextDueLesson ? getDaysRemaining(nextDueLesson.DueDate) : 0;

  const progressPercentage =
    metrics.totalLessons > 0
      ? (metrics.lessonsCompleted / metrics.totalLessons) * 100
      : 0;

  // Streak Calculation Logic
  const frequencyDays = (() => {
    // Attempt to access commonly named properties for frequency
    const freq = progressData.lesson_frequency || progressData.user?.lesson_frequency || progressData.frequency || "7";
    const match = String(freq).match(/\d+/);
    return match ? parseInt(match[0]) : 7;
  })();

  const currentCycleStreak = (() => {
    const history = progressData.completedLessonsByUser || [];
    if (!history.length) return 0;

    const completionTimestamps = history
      .map((l) => {
        const p = Array.isArray(l.progress) ? l.progress[0] : l.progress;
        return p?.completedAt ? new Date(p.completedAt).getTime() : null;
      })
      .filter(Boolean)
      .sort((a, b) => a - b);

    if (!completionTimestamps.length) return 0;

    const firstCompletion = completionTimestamps[0];
    const freqMs = frequencyDays * 24 * 60 * 60 * 1000;
    const cycleIndexes = new Set();

    completionTimestamps.forEach((ts) => {
      const diff = ts - firstCompletion;
      // Cycle 0 is the start. Cycle index = floor(diff / freq)
      const cycleIdx = Math.floor(diff / freqMs);
      if (cycleIdx >= 0) cycleIndexes.add(cycleIdx);
    });

    const maxCycleIdx = Math.max(...cycleIndexes);
    let streakCount = 0;

    // Count backwards contiguous block
    for (let i = maxCycleIdx; i >= 0; i--) {
      if (cycleIndexes.has(i)) {
        streakCount++;
      } else {
        break;
      }
    }

    // Check if the current cycle window (or previous) has expired without completion
    // The "current" cycle index based on NOW
    const now = Date.now();
    const endingOfNextCycle = firstCompletion + (maxCycleIdx + 2) * freqMs; // End of Is (max+1)

    // If we are past the end of the next cycle (max+1), and we haven't completed it (since maxCycleIdx is the last one),
    // then the streak is broken.
    if (now > endingOfNextCycle) {
      return 0;
    }

    return streakCount;
  })();

  return (
    <div className="space-y-6 max-w-8xl mx-auto">
      {/* Overall Training Metrics Section */}
      <div className="bg-[#FFFBF5] -mx-6 -mt-6 p-6 border-b border-[#FDE6D2]">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-black text-[#111827] tracking-tight">
              Overall Training Metrics
            </h1>
            {loading && <div className="animate-spin h-4 w-4 border-2 border-primary/20 border-t-primary rounded-full" />}
          </div>
          <div className="flex items-center gap-4">
            {isEmployee && restaurants.length > 0 && (
              <Dropdown
                label={restaurants.find(r => (r.uuid || r.id) === selectedRestaurant)?.name || "Select Restaurant"}
                options={restaurants.map(r => ({ value: r.uuid || r.id, label: r.name }))}
                selectedOption={selectedRestaurant}
                onSelect={(option) => setSelectedRestaurant(option.value)}
                buttonClassName="h-9 px-4 py-2 bg-white border border-[#FDE6D2] text-[#111827] rounded-lg font-bold text-xs shadow-sm hover:bg-gray-50 transition-all"
                searchable
                searchPlaceholder="Search restaurant..."
              />
            )}
            <div className="flex items-center gap-1.5 text-gray-500 text-xs font-semibold">
              <Clock size={14} className="text-gray-400" />
              <span className="ml-1">Last updated: {progressData.lastUpdated ? new Date(progressData.lastUpdated).toLocaleString() : "—"}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Card 1: Overall Progress */}
          <div className="bg-white rounded-xl border border-[#F3E8E0] p-4 flex flex-col items-center justify-center shadow-sm hover:shadow-md transition-shadow h-40">
            <div className="mb-1.5">
              <CircularProgress
                value={metrics.overallProgress}
                size={85}
                strokeWidth={9}
              />
            </div>
            <h3 className="text-[13px] font-bold text-[#111827] tracking-tight mb-0 mt-3">Overall Progress</h3>
            <p className="text-[12px] text-gray-400 font-semibold tracking-tighter line-clamp-1">Great momentum!</p>
          </div>

          {/* Card 2: Lessons Completed */}
          <div className="bg-white rounded-xl border border-[#F3E8E0] p-4 flex flex-col items-center justify-center shadow-sm hover:shadow-md transition-shadow h-40">
            <div className="mb-1.5">
              <CircularProgress
                value={metrics.lessonsCompleted}
                max={metrics.totalLessons}
                size={85}
                strokeWidth={9}
                centerContent={
                  <div className="flex flex-col items-center leading-none">
                    <span className="text-2xl font-black text-[#111827]">{metrics.lessonsCompleted}</span>
                    <span className="text-[12px] font-bold text-gray-400 mt-0.5">of {metrics.totalLessons}</span>
                  </div>
                }
              />
            </div>
            <h3 className="text-[13px] font-bold text-[#111827] tracking-tight mb-0 mt-3 text-center">Lessons Completed</h3>
            <p className="text-[12px] text-gray-400 font-semibold tracking-tighter line-clamp-1">{metrics.totalLessons - metrics.lessonsCompleted} remaining</p>
          </div>

          {/* Card 3: Average Score */}
          <div className="bg-white rounded-xl border border-[#F3E8E0] p-4 flex flex-col items-center justify-center shadow-sm hover:shadow-md transition-shadow h-40">
            <div className="mb-0">
              <span className="text-3xl font-black text-[#881337]">{metrics.averageScore}%</span>
            </div>
            <h3 className="text-[13px] font-bold text-[#111827] tracking-tight mb-0 mt-5 text-center">Average Score</h3>
            <p className="text-[12px] text-gray-400 font-semibold tracking-tighter">Excellent work!</p>
          </div>

          {/* Card 4: Average Attempts */}
          <div className="bg-white rounded-xl border border-[#F3E8E0] p-4 flex flex-col items-center justify-center shadow-sm hover:shadow-md transition-shadow h-40">
            <div className="mb-0">
              <span className="text-3xl font-black text-[#881337]">{metrics.averageAttempts}</span>
            </div>
            <h3 className="text-[13px] font-bold text-[#111827] tracking-tight mb-0 mt-5 text-center">Average Attempts</h3>
            <p className="text-[12px] text-gray-400 font-semibold tracking-tighter">per chapter</p>
          </div>
        </div>

        {/* New Row: Consistency & Lesson Mix */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Consistency Card */}
          <div className="bg-white rounded-xl border border-[#F3E8E0] p-5 shadow-sm flex flex-col justify-between">
            <div className="flex justify-between items-start">
              <div className="text-left">
                <h3 className="text-lg font-bold text-[#111827]">Consistency</h3>
                <p className="text-sm text-gray-400 font-semibold">Keep up the momentum</p>
              </div>
              <div className="p-1.5 bg-[#E6F4EA] rounded-lg text-[#137333]">
                <Flame size={18} fill="currentColor" />
              </div>
            </div>

            <div className="my-2.5 text-left">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-black text-[#881337]">{currentCycleStreak}</span>
                <span className="text-base font-black text-gray-700">Training Streak: {currentCycleStreak} cycle{currentCycleStreak !== 1 ? 's' : ''}</span>
              </div>
              <p className="text-sm text-gray-500 font-semibold mt-0.5">
                {currentCycleStreak > 0
                  ? `You've completed your training within the expected timeframe for ${currentCycleStreak} cycle${currentCycleStreak !== 1 ? 's' : ''} in a row.`
                  : "Start a streak by completing your scheduled lessons."}
              </p>
            </div>

            <div className="pt-3 border-t border-gray-100 flex justify-between items-end">
              <div className="text-[12px] font-semibold text-gray-500 ">
                Cycle length:<span className="text-gray-900 ml-1">{frequencyDays} days</span>
              </div>
            </div>
          </div>

          {/* Lesson Mix Card */}
          <div className="bg-white rounded-xl border border-[#F3E8E0] p-5 shadow-sm flex flex-col items-center">
            <div className="w-full text-left mb-2">
              <h3 className="text-sm font-semibold text-gray-500">Lesson Mix</h3>
            </div>

            <div className="relative flex items-center justify-center mb-6">
              <svg width="110" height="110" viewBox="0 0 100 100" className="transform -rotate-90">
                {/* Wine Segment */}
                <circle
                  cx="50" cy="50" r="40"
                  fill="transparent"
                  stroke="#D1A181"
                  strokeWidth="15"
                  strokeDasharray={`${wineArc} ${baseArc}`}
                />
                {/* Food Segment */}
                <circle
                  cx="50" cy="50" r="40"
                  fill="transparent"
                  stroke="#881337"
                  strokeWidth="15"
                  strokeDasharray={`${foodArc} ${baseArc}`}
                  strokeDashoffset={`-${wineArc}`}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xs font-black text-[#111827]">{totalCompleted} total</span>
              </div>
            </div>

            <div className="w-full space-y-2 px-4">
              <div className="flex justify-between items-center text-[12px]">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#881337]"></div>
                  <span className="font-bold text-gray-600">Food</span>
                </div>
                <div className="font-bold text-gray-900">
                  {foodPercentage}% ({foodCount})
                </div>
              </div>
              <div className="flex justify-between items-center text-[12px]">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#D1A181]"></div>
                  <span className="font-bold text-gray-600">Wine</span>
                </div>
                <div className="font-bold text-gray-900">
                  {winePercentage}% ({wineCount})
                </div>
              </div>
            </div>

            <p className="text-[12px] text-gray-400 font-bold  mt-6 text-center w-full">
              Distribution of completed lessons
            </p>
          </div>
        </div>
      </div>

      {/* Food and Wine Lessons Progress */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Food Lessons Card */}
        <div className="bg-white rounded-[2rem] shadow-sm border border-[#F3E8E0] overflow-hidden">
          {/* Header */}
          <div className="bg-[#881337] p-3.5 px-5 flex justify-between items-center text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <UtensilsCrossed size={20} className="text-white" />
              </div>
              <h2 className="text-base font-black tracking-tight">Food Lessons</h2>
            </div>
            <div className="bg-white/20 px-3 py-1 rounded-full text-[12px] font-black">
              {progressSummary.food.chaptersCompleted}/{progressSummary.food.totalChapters} Chapters
            </div>
          </div>

          <div className="p-5 space-y-4">
            <div>
              <div className="flex justify-between items-end mb-2">
                <span className="text-xs font-black text-[#111827]">Chapters</span>
                <span className="text-xs font-black text-[#881337]">{progressSummary.food.totalChapters ? Math.round((progressSummary.food.chaptersCompleted / progressSummary.food.totalChapters) * 100) : 0}%</span>
              </div>
              <div className="h-2 w-full bg-[#F3E8E0] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#881337] rounded-full transition-all duration-500"
                  style={{ width: `${progressSummary.food.totalChapters ? (progressSummary.food.chaptersCompleted / progressSummary.food.totalChapters) * 100 : 0}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#FCF7F2] p-3 rounded-xl">
                <p className="text-[12px] font-bold text-gray-400  tracking-wider mb-0.5">Average Score</p>
                <p className="text-lg font-black text-[#111827]">{progressSummary.food.averageScore}%</p>
              </div>
              <div className="bg-[#FCF7F2] p-3 rounded-xl">
                <p className="text-[12px] font-bold text-gray-400  tracking-wider mb-0.5 whitespace-nowrap overflow-hidden text-ellipsis">Avg. Attempts</p>
                <p className="text-lg font-black text-[#111827]">{progressSummary.food.averageAttempts}</p>
              </div>
              <div className="bg-[#FCF7F2] p-3 rounded-xl">
                <p className="text-[12px] font-bold text-gray-400  tracking-wider mb-0.5">Units Completed</p>
                <p className="text-lg font-black text-[#111827]">{progressSummary.food.unitsCompleted}/{progressSummary.food.totalUnits}</p>
              </div>
              <div className="bg-[#FCF7F2] p-3 rounded-xl">
                <p className="text-[12px] font-bold text-gray-400  tracking-wider mb-0.5">Total Time</p>
                <p className="text-lg font-black text-[#111827]">{progressData.food?.foodTotalTimeFormatted || progressData.totalTimeSpentFormatted || '0m'}</p>
              </div>
            </div>


          </div>
        </div>

        {/* Wine Lessons Card */}
        <div className="bg-white rounded-[2rem] shadow-sm border border-[#F3E8E0] overflow-hidden">
          {/* Header */}
          <div className="bg-[#D1A181] p-3.5 px-5 flex justify-between items-center text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Wine size={20} className="text-white" />
              </div>
              <h2 className="text-base font-black tracking-tight">Wine Lessons</h2>
            </div>
            <div className="bg-white/20 px-3 py-1 rounded-full text-[12px] font-black">
              {progressSummary.wine.chaptersCompleted}/{progressSummary.wine.totalChapters} Chapters
            </div>
          </div>

          <div className="p-5 space-y-4">
            <div>
              <div className="flex justify-between items-end mb-2">
                <span className="text-xs font-black text-[#111827]">Chapters</span>
                <span className="text-xs font-black text-[#D1A181]">{progressSummary.wine.totalChapters ? Math.round((progressSummary.wine.chaptersCompleted / progressSummary.wine.totalChapters) * 100) : 0}%</span>
              </div>
              <div className="h-2 w-full bg-[#F3E8E0] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#D1A181] rounded-full transition-all duration-500"
                  style={{ width: `${progressSummary.wine.totalChapters ? (progressSummary.wine.chaptersCompleted / progressSummary.wine.totalChapters) * 100 : 0}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#FCF7F2] p-3 rounded-xl">
                <p className="text-[12px] font-bold text-gray-400  tracking-wider mb-0.5">Average Score</p>
                <p className="text-lg font-black text-[#111827]">{progressSummary.wine.averageScore}%</p>
              </div>
              <div className="bg-[#FCF7F2] p-3 rounded-xl">
                <p className="text-[12px] font-bold text-gray-400  tracking-wider mb-0.5 whitespace-nowrap overflow-hidden text-ellipsis">Avg. Attempts</p>
                <p className="text-lg font-black text-[#111827]">{progressSummary.wine.averageAttempts}</p>
              </div>
              <div className="bg-[#FCF7F2] p-3 rounded-xl">
                <p className="text-[12px] font-bold text-gray-400  tracking-wider mb-0.5">Units Completed</p>
                <p className="text-lg font-black text-[#111827]">{progressSummary.wine.unitsCompleted}/{progressSummary.wine.totalUnits}</p>
              </div>
              <div className="bg-[#FCF7F2] p-3 rounded-xl">
                <p className="text-[12px] font-bold text-gray-400  tracking-wider mb-0.5">Total Time</p>
                <p className="text-lg font-black text-[#111827]">{progressData.wine?.wineTotalTimeFormatted || '0m'}</p>
              </div>
            </div>


          </div>
        </div>
      </div>

      {/* Achievements & Badges Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-black text-[#111827]">Achievements & Badges</h2>
          <button className="text-[#881337] text-xs font-black hover:underline  tracking-wider">View All</button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {progressData.userBadges && progressData.userBadges.length > 0 ? (
            progressData.userBadges.slice(0, 8).map((b, i) => (
              <div key={b.badge_id || i} className="bg-white rounded-[1.5rem] border border-[#F3E8E0] p-4 flex flex-col items-center justify-center text-center shadow-sm">
                <div className="w-20 h-20 flex items-center justify-center mx-auto mb-3">
                  {b.badge_image ? (
                    <img src={process.env.REACT_APP_IMAGE_BASE_URL + b.badge_image} alt={b.badge_name} className="w-full h-full object-contain" />
                  ) : (
                    <div className="text-4xl">🏅</div>
                  )}
                </div>
                <h3 className="text-[13px] font-black text-[#111827] mb-0.5">{b.badge_name}</h3>
                <p className="text-[12px] font-bold text-gray-400">{b.earned_at ? new Date(b.earned_at).toLocaleDateString() : ''}</p>
              </div>
            ))
          ) : (
            <div className="col-span-4 bg-white rounded-[1.5rem] border border-[#F3E8E0] p-6 text-center shadow-sm">
              <p className="text-sm font-bold text-gray-500">No badges yet. Complete lessons to earn rewards!</p>
            </div>
          )}
        </div>
      </div>

      {/* Completed Lessons Table */}
      <div className="grid grid-cols-1 ">
        <div className="lg:col-span-2 bg-white rounded-[2rem] shadow-sm border border-[#F3E8E0] overflow-hidden">
          <div className="p-6">
            <CompletedLessonsTable lessons={completedLessons} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingProgressPage;
